/**
 * V78 — Makeover premium sections: Why Learn with Ishpreet, Why Choose Anand
 * Tutorials, Trust ("Our Commitment"), and future-ready Testimonials.
 *
 * All sample testimonials are clearly labelled 'SAMPLE PREVIEW' — they exist
 * for layout/design purposes only and are NOT real client statements. When
 * real testimonials are added via the backend `/api/makeover/testimonials`
 * admin endpoints, the sample block is auto-hidden.
 *
 * Fully additive — dropped into MakeoverLanding between categories and FAQ.
 */
import { useEffect, useState } from "react";
import {
    Sparkles, Users, Target, MonitorPlay, TrendingUp, Award,
    Scissors, Palette, Droplet, Shirt, ShoppingBag, Clock,
    Globe2, Lock, Baby, Compass, Video, Calendar, CheckCircle2,
    Headphones, Gift, Shield, ShieldCheck, Star, ExternalLink,
} from "lucide-react";
import axios from "axios";
import { useSettings } from "../context/SettingsContext";
import { MAKEOVER_EXPERT } from "../data/makeoverData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Verified Google Business Profile — source of truth for any social-proof block.
// Only the data below is authenticated: business name + rating + reviews URL.
// Individual review text/reviewer names are NEVER fabricated — they live on
// Google, and users see them there via the "View All Reviews on Google" CTA.
const GOOGLE_BUSINESS = {
    name: "Makeovers By Ishpreet Kaur — Bridal Makeup Artist in Jabalpur",
    rating: 4.7,
    reviewsUrl: "https://maps.app.goo.gl/MH7PNjcB8BLdibKt6",
};

/* ═════════════════════════════════════════════════════════════════════
   DATA
   ═════════════════════════════════════════════════════════════════════ */
const WHY_LEARN = [
    { icon: Users, t: "Personalized 1-on-1 Live Guidance", d: "Every session is you + Ishpreet on live camera. No batches, no shared attention, no group calls." },
    { icon: Target, t: "Individual Attention", d: "Your face, wardrobe, skin, goals — the roadmap is built around YOU, not a template." },
    { icon: Compass, t: "Customized Makeover Roadmap", d: "A structured 4-8 session journey tailored to your starting point and outcome." },
    { icon: MonitorPlay, t: "Practical Live Learning", d: "Real-time practice on your own face + wardrobe. Learn by DOING with expert feedback." },
    { icon: TrendingUp, t: "Professional Image Enhancement", d: "Executive presence, first-impression audit, business-appropriate styling for interviews + client-facing roles." },
    { icon: Award, t: "Confidence Building", d: "Body language drills, voice modulation, self-presentation — measurable growth session-over-session." },
    { icon: Scissors, t: "Personal Grooming", d: "Daily grooming rituals, hair health, hygiene mastery — customized to your routine and skin type." },
    { icon: Palette, t: "Makeup Training", d: "Everyday, office, occasion and bridal fundamentals — camera-based practice on YOUR face." },
    { icon: Droplet, t: "Skincare Guidance", d: "Skin-type diagnosis, product decoding, morning/night routines calibrated to your concerns." },
    { icon: Shirt, t: "Personal Styling", d: "Body-shape + colour-season analysis, silhouette rules, capsule wardrobe design." },
    { icon: ShoppingBag, t: "Wardrobe Consultation", d: "Live closet audit on camera, gap analysis and curated shopping lists within your budget." },
    { icon: Clock, t: "Flexible Time Zones", d: "GMT, EST, PST, GST, SGT, AEST, IST — sessions scheduled around YOUR local evening/weekend." },
    { icon: Globe2, t: "Worldwide & Pan India Support", d: "Clients across USA, UK, UAE, Canada, Australia, Singapore, Malaysia + every Indian state." },
    { icon: Lock, t: "Private & Comfortable Learning", d: "1-on-1 secure Zoom rooms. Recordings shared privately only with you. Zero group exposure." },
    { icon: Baby, t: "Beginner Friendly", d: "No prior knowledge needed. First 10 minutes calibrate pacing to your comfort level." },
    { icon: Sparkles, t: "Continuous Guidance", d: "Between-session WhatsApp check-ins + practice prompts to keep momentum going." },
];

const WHY_CHOOSE_AT = [
    { icon: Video, t: "100% Live One-on-One Sessions", d: "Every class is live over Zoom/Google Meet — never recorded courses, never batches." },
    { icon: Target, t: "Personalized Learning Experience", d: "Curriculum built around your goals, learning speed and outcome preferences." },
    { icon: Award, t: "Dedicated Makeover Expert", d: "You work with Ishpreet Kaur, our certified 1-on-1 makeover expert — not a rotating tutor pool." },
    { icon: Calendar, t: "Flexible Scheduling", d: "Weekday evenings, weekend-only, or mornings — you decide, we schedule." },
    { icon: Globe2, t: "International Time Zone Support", d: "USA, UK, UAE, Canada, Australia, Singapore, Malaysia and more — matched to your timezone." },
    { icon: MonitorPlay, t: "Pan India & Worldwide Availability", d: "Serve clients across all Indian metros + Tier-2 cities and 14 international markets." },
    { icon: CheckCircle2, t: "Professional Guidance", d: "Certified image consultants, professional makeup artists, hairstylists and personality coaches." },
    { icon: Lock, t: "Private Learning Environment", d: "Secure 1-on-1 sessions. Recordings shared only with you. Zero group exposure." },
    { icon: Headphones, t: "Easy WhatsApp Support", d: "Between-session queries handled via direct WhatsApp — no ticket systems or chatbots." },
    { icon: Gift, t: "Free Consultation / Demo", d: "First discovery call is free. Zero commitment, zero payment upfront." },
    { icon: Sparkles, t: "Premium Learning Experience", d: "Structured session plans, homework, private recording library, progress reviews." },
    { icon: Shield, t: "Secure Online Sessions", d: "End-to-end encrypted meeting rooms + private link — never posted publicly." },
];

const TRUST_CARDS = [
    { icon: Target, t: "Personalized Guidance", d: "Every roadmap is built around your face, wardrobe, skin, timezone and goals — no templates." },
    { icon: Lock, t: "Privacy & Confidentiality", d: "Session recordings + personal images stay strictly private. Never posted, shared, or resold." },
    { icon: Award, t: "Professional Coaching", d: "Certified image consultants, professional makeup artists and personality development coaches only." },
    { icon: Video, t: "Live Interactive Sessions", d: "Two-way HD video, camera-based practice, real-time feedback. Nothing pre-recorded." },
    { icon: Calendar, t: "Flexible Scheduling", d: "Sessions built around your calendar. Reschedule without penalty; pause anytime." },
    { icon: Globe2, t: "Global Support", d: "Worldwide clients in 15+ markets, coordinated through a single WhatsApp point of contact." },
    { icon: MonitorPlay, t: "Practical Learning", d: "You practice with the expert on camera every session — reproducible skill, not passive listening." },
    { icon: TrendingUp, t: "Long-Term Confidence Building", d: "Measurable session-over-session growth in styling, grooming, presence and self-assurance." },
];

// V79 — Sample testimonials removed. Per brand-integrity mandate, we NEVER
// fabricate or paraphrase client quotes. Real client testimonials live on
// Ishpreet Kaur's verified Google Business Profile; we display only the
// authenticated aggregate rating + a direct link there. When admin manually
// adds real testimonials (transcribed from Google or written with client
// consent) via the /api/admin/makeover/testimonials endpoints, those replace
// this Google badge block.

/* ═════════════════════════════════════════════════════════════════════
   HELPER COMPONENTS
   ═════════════════════════════════════════════════════════════════════ */
function GridCard({ icon: Icon, t, d, tone = "rose", i }) {
    const tones = {
        rose: "hover:border-rose-300 hover:shadow-rose-100/60",
        amber: "hover:border-amber-300 hover:shadow-amber-100/60",
        slate: "hover:border-slate-300 hover:shadow-slate-100/60",
    };
    return (
        <div
            data-testid={`grid-card-${i}`}
            style={{ animationDelay: `${(i % 6) * 50}ms` }}
            className={`group rounded-2xl border border-slate-200 bg-white p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl ${tones[tone]}`}
        >
            <div className={`w-10 h-10 rounded-xl bg-${tone}-50 border border-${tone}-100 text-${tone}-600 flex items-center justify-center mb-3 group-hover:scale-105 transition-transform`}>
                <Icon className="w-5 h-5" />
            </div>
            <h3 className="font-heading font-bold text-slate-900 mb-1 text-sm leading-tight">{t}</h3>
            <p className="text-xs text-slate-600 leading-relaxed">{d}</p>
        </div>
    );
}

function TestimonialCard({ t, i }) {
    return (
        <article
            data-testid={`makeover-testimonial-${i}`}
            className="relative rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm hover:shadow-lg transition-shadow"
        >
            <div className="flex items-center gap-1 mb-3">
                {Array.from({ length: 5 }).map((_, r) => (
                    <Star key={r} className={`w-3.5 h-3.5 ${r < (t.rating || 5) ? "text-amber-400 fill-amber-400" : "text-slate-200"}`} />
                ))}
            </div>
            <p className="text-sm text-slate-700 leading-relaxed mb-4 italic">&ldquo;{t.body}&rdquo;</p>
            <div className="pt-4 border-t border-slate-100">
                <div className="font-heading font-bold text-sm text-slate-900">{t.name}</div>
                <div className="text-[11px] text-slate-500">
                    {[t.country, t.service, t.date].filter(Boolean).join(" · ")}
                </div>
            </div>
        </article>
    );
}

/**
 * VerifiedGoogleBadge — the fallback block shown when the admin
 * `makeover_testimonials` collection is empty. Displays only authenticated
 * social proof (aggregate rating + business name from Google Business
 * Profile) and directs users to the actual Google reviews page.
 * ZERO fabrication.
 */
function VerifiedGoogleBadge({ reviewsUrl }) {
    return (
        <div
            data-testid="verified-google-reviews"
            className="mx-auto max-w-3xl bg-white border border-slate-200 rounded-3xl p-8 sm:p-10 shadow-lg shadow-slate-100/50 text-center"
        >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-[10px] uppercase tracking-[0.22em] font-bold text-blue-700 mb-4">
                <ShieldCheck className="w-3 h-3" /> Verified · Google Business Profile
            </div>
            <div className="flex items-center justify-center gap-2 mb-2">
                <div className="font-heading text-5xl sm:text-6xl font-bold text-slate-900 tabular-nums">{GOOGLE_BUSINESS.rating}</div>
                <div className="flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, r) => {
                        const filled = r + 1 <= Math.floor(GOOGLE_BUSINESS.rating);
                        const half = r + 0.5 < GOOGLE_BUSINESS.rating && r + 1 > GOOGLE_BUSINESS.rating;
                        return (
                            <Star
                                key={r}
                                className={`w-6 h-6 ${filled || half ? "text-amber-400 fill-amber-400" : "text-slate-200"}`}
                            />
                        );
                    })}
                </div>
            </div>
            <div className="font-heading font-bold text-lg text-slate-900 mb-1">
                {GOOGLE_BUSINESS.name}
            </div>
            <div className="text-xs text-slate-500 mb-6">
                Live rating sourced from Google Business Profile
            </div>
            <a
                href={reviewsUrl || GOOGLE_BUSINESS.reviewsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-reviews-cta"
                className="inline-flex items-center gap-2 bg-[#4285F4] hover:bg-[#3367D6] text-white font-bold px-6 py-3 rounded-xl text-sm shadow-md shadow-blue-200/50"
            >
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                </svg>
                View All Reviews on Google
                <ExternalLink className="w-4 h-4 opacity-80" />
            </a>
            <p className="mt-5 text-xs text-slate-500 max-w-md mx-auto leading-relaxed">
                Every review is written by verified Google users on Ishpreet Kaur&apos;s official Google Business Profile.
                No testimonials on this website are fabricated, paraphrased, or AI-generated.
            </p>
        </div>
    );
}

/* ═════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═════════════════════════════════════════════════════════════════════ */
export default function MakeoverPremiumSections({
    showWhyLearn = true,
    showWhyChoose = true,
    showTrust = true,
    showTestimonials = true,
} = {}) {
    const { settings } = useSettings();
    const [testimonials, setTestimonials] = useState([]);
    const [loaded, setLoaded] = useState(false);

    // Reviews URL is admin-editable via settings → makeover_expert_google_maps_url.
    // Default is Ishpreet Kaur's Google Business Profile.
    const reviewsUrl = settings?.makeover_expert_google_maps_url || MAKEOVER_EXPERT.defaults.googleMapsUrl || GOOGLE_BUSINESS.reviewsUrl;

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${API}/makeover/testimonials/public`);
                if (!cancelled) setTestimonials(res.data?.items || []);
            } catch { /* ignore — fallback badge will show */ }
            finally { if (!cancelled) setLoaded(true); }
        })();
        return () => { cancelled = true; };
    }, []);

    const hasRealTestimonials = loaded && testimonials.length > 0;

    return (
        <>
            {/* ═══ 1 · Why Learn with Ishpreet Kaur ═══ */}
            {showWhyLearn && (
            <section data-testid="section-why-learn-ishpreet" className="py-16 sm:py-20 bg-gradient-to-b from-white via-rose-50/40 to-white">
                <div className="max-w-6xl mx-auto px-6 lg:px-8">
                    <div className="text-center mb-10 max-w-2xl mx-auto">
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-rose-600 mb-2 inline-flex items-center gap-2">
                            <Sparkles className="w-3 h-3" /> Why Learn with Her
                        </div>
                        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-3">
                            Why Learn with <span className="text-rose-600">Ishpreet Kaur?</span>
                        </h2>
                        <p className="text-slate-600 text-sm sm:text-base">
                            Ishpreet Kaur is our dedicated 1-on-1 Live Online Makeover Expert serving worldwide + Pan India clients. Every session is designed around YOU — your face, your wardrobe, your timezone, your goals.
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        {WHY_LEARN.map((c, i) => <GridCard key={c.t} {...c} tone="rose" i={i} />)}
                    </div>
                </div>
            </section>
            )}

            {/* ═══ 2 · Why Choose Anand Tutorials ═══ */}
            {showWhyChoose && (
            <section data-testid="section-why-choose-at" className="py-16 sm:py-20 bg-white border-t border-slate-100">
                <div className="max-w-6xl mx-auto px-6 lg:px-8">
                    <div className="text-center mb-10 max-w-2xl mx-auto">
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-600 mb-2 inline-flex items-center gap-2">
                            <ShieldCheck className="w-3 h-3" /> Why Choose Us
                        </div>
                        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-3">
                            Why Choose <span className="text-amber-600">Anand Tutorials</span> for<br className="hidden sm:block" /> 1-on-1 Live Online Makeover Classes?
                        </h2>
                        <p className="text-slate-600 text-sm sm:text-base">
                            12+ years of running personalised 1-on-1 education. Now extending that same rigour to online makeover coaching — private, live, and completely tailored.
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        {WHY_CHOOSE_AT.map((c, i) => <GridCard key={c.t} {...c} tone="amber" i={i} />)}
                    </div>
                </div>
            </section>
            )}

            {/* ═══ 3 · Trust · Our Commitment ═══ */}
            {showTrust && (
            <section data-testid="section-trust" className="py-16 sm:py-20 bg-gradient-to-br from-slate-900 via-slate-900 to-rose-950 text-white">
                <div className="max-w-6xl mx-auto px-6 lg:px-8">
                    <div className="text-center mb-10 max-w-2xl mx-auto">
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-rose-300 mb-2 inline-flex items-center gap-2">
                            <ShieldCheck className="w-3 h-3" /> Our Commitment
                        </div>
                        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3">
                            Trust, delivered every session.
                        </h2>
                        <p className="text-slate-300 text-sm sm:text-base">
                            These are the promises behind every 1-on-1 live online makeover session with Anand Tutorials.
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        {TRUST_CARDS.map((c, i) => (
                            <div
                                key={c.t}
                                data-testid={`trust-card-${i}`}
                                style={{ animationDelay: `${(i % 4) * 60}ms` }}
                                className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-5 hover:bg-white/10 hover:border-rose-400/30 transition-all duration-300"
                            >
                                <div className="w-10 h-10 rounded-xl bg-rose-500/20 border border-rose-400/30 text-rose-300 flex items-center justify-center mb-3">
                                    <c.icon className="w-5 h-5" />
                                </div>
                                <h3 className="font-heading font-bold text-white mb-1 text-sm leading-tight">{c.t}</h3>
                                <p className="text-xs text-slate-300 leading-relaxed">{c.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            )}

            {/* ═══ 4 · Testimonials (real-only · Google Business verified) ═══ */}
            {showTestimonials && (
            <section data-testid="section-testimonials" className="py-16 sm:py-20 bg-white border-t border-slate-100">
                <div className="max-w-6xl mx-auto px-6 lg:px-8">
                    <div className="text-center mb-8 max-w-2xl mx-auto">
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-rose-600 mb-2 inline-flex items-center gap-2">
                            <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> Genuine Reviews
                        </div>
                        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-3">
                            What real clients say.
                        </h2>
                        <p className="text-slate-600 text-sm sm:text-base">
                            Ishpreet Kaur&apos;s reviews come directly from her verified Google Business Profile.
                            We display only authentic, attributable client feedback &mdash; nothing fabricated.
                        </p>
                    </div>

                    {hasRealTestimonials ? (
                        <>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mb-8">
                                {testimonials.slice(0, 9).map((t, i) => (
                                    <TestimonialCard key={t.id || i} t={t} i={i} />
                                ))}
                            </div>
                            <div className="text-center">
                                <a
                                    href={reviewsUrl}
                                    target="_blank" rel="noopener noreferrer"
                                    data-testid="google-reviews-cta-secondary"
                                    className="inline-flex items-center gap-2 bg-[#4285F4] hover:bg-[#3367D6] text-white font-bold px-5 py-2.5 rounded-xl text-sm"
                                >
                                    View More Reviews on Google <ExternalLink className="w-4 h-4 opacity-80" />
                                </a>
                            </div>
                        </>
                    ) : (
                        <VerifiedGoogleBadge reviewsUrl={reviewsUrl} />
                    )}
                </div>
            </section>
            )}
        </>
    );
}
